import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { bmxurl, ConvertDateToDB, getAccess } from 'src/globals';
import { BusyService } from '../../_services/busy.service';
import { GlobalService } from '../../_services/global.service';
import { users_returndata } from 'src/types/user_returndata';
import { booking_returndata } from 'src/types/booking_returndata';
import { tag_returndata } from 'src/types/tag_returndata';
import { tag } from 'src/types/tag';

//One admission type as a column on the report. The key is what the row is indexed by, because an
//admission's name can hold anything - a dot in it would be read as a path by the table.
interface admissioncolumn {
  key: string;
  name: string;
  total: number;
}

@Component({
  selector: 'app-admissionreport',
  templateUrl: './admissionreport.component.html',
  styleUrls: ['./admissionreport.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdmissionreportComponent implements OnInit {

  VenueID: string = localStorage.getItem("SmartVenueID")!;
  httpResponse: any;
  AccessAllowed: boolean = false;
  CompletedLoading: boolean = false;
  CurrencySymbol: string = localStorage.getItem("Currency") == null ? "GBP" : localStorage.getItem("Currency")!;

  FromDate: Date = new Date();
  ToDate: Date = new Date();

  tags: tag[] = [];
  chosentags: tag[] = [];

  ListBookings: any[] = [];
  //One per admission type found in the period, so a venue only sees the ones it actually sells.
  admissionColumns: admissioncolumn[] = [];

  guests: number = 0;
  total: number = 0;
  outstanding: number = 0;

  cols: any[] = [];

  constructor(
    private http: HttpClient,
    private router: Router,
    private messageService: MessageService,
    private busyService: BusyService,
    private globalService: GlobalService) { }

  ngOnInit(): void {

    this.globalService.setSmallScreenMenu(false);

    let userid = localStorage.getItem("SmartUserID");

    this.http.get(bmxurl + "User/" + userid).subscribe(response => {
      this.httpResponse = (response as users_returndata);

      if (this.httpResponse.succeeded) {
        this.AccessAllowed = getAccess("REPORTS", this.httpResponse.data) == true;

        this.loadTags();
        this.loadgrid();
      }

      this.CompletedLoading = true;
    }, error => {
      this.CompletedLoading = true;
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting User.'});
    });
  }

  loadTags() {
    this.http.get(bmxurl + "TagList/" + this.VenueID + "/false").subscribe(response => {
      this.tags = (response as tag_returndata).data;
    }, error => {
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting Tags.'});
    });
  }

  doneDate() {
    this.loadgrid();
  }

  editdata(bookingId: string) {
    this.router.navigateByUrl("/editbooking/" + bookingId);
  }

  loadgrid() {
    this.busyService.busy();

    let searchCrit = {
      venueid: this.VenueID,
      startdate: ConvertDateToDB(this.FromDate),
      enddate: ConvertDateToDB(this.ToDate),
      tags: this.chosentags,
      disabled: false,
      created: true
    };

    this.http.post(bmxurl + "/BookingList", searchCrit).subscribe(response => {
      const bookings = ((response as booking_returndata).data ?? [])
        .filter((b: any) => b.inProgress == false);

      this.buildRows(bookings);
      this.busyService.idle();
    }, error => {
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Error getting bookings', detail:'An error occurred getting Bookings.'});
    });
  }

  private buildRows(bookings: any[]) {
    this.guests = 0;
    this.total = 0;
    this.outstanding = 0;

    //Every admission type seen in the period, in the order it was first met, so the columns do
    //not jump about between runs of the same report.
    const columns: admissioncolumn[] = [];

    const columnFor = (name: string): admissioncolumn => {
      let found = columns.find(column => column.name == name);

      if (found == null) {
        found = { key: "adm" + columns.length, name: name, total: 0 };
        columns.push(found);
      }

      return found;
    };

    bookings.forEach(booking => {
      booking.startTimeCSV = this.formatDateForExcel(booking.startTime);
      booking.createddateCSV = this.formatDateForExcel(booking.createddate);

      this.guests += Number(booking.guests) || 0;
      this.total += Number(booking.total) || 0;
      this.outstanding += Number(booking.outstanding) || 0;

      this.admissionsOn(booking).forEach(adm => {
        const column = columnFor(adm.name);

        //Put straight on the row, which is what the table and the CSV export both read.
        booking[column.key] = (Number(booking[column.key]) || 0) + adm.people;
        column.total += adm.people;
      });
    });

    this.admissionColumns = columns;
    this.ListBookings = bookings;

    this.cols = [
      { field: 'bookingIdHR', header: 'Sales ID' },
      { field: 'startTimeCSV', header: 'Start Date/Time' },
      { field: 'name', header: 'Name' },
      { field: 'email', header: 'Email' },
      { field: 'phone', header: 'Phone' },
      { field: 'packageName', header: 'Package' },
      ...columns.map(column => ({ field: column.key, header: column.name })),
      { field: 'guests', header: 'Guests' },
      { field: 'total', header: 'Total' },
      { field: 'outstanding', header: 'Outstanding' },
    ];
  }

  //A booking's admissions arrive either as the objects themselves or as the line the API writes
  //for display - "2 x Adults, 1 x Child" - so both are read.
  private admissionsOn(booking: any): { name: string, people: number }[] {
    if (Array.isArray(booking.admissions)) {
      return booking.admissions
        .filter((adm: any) => (Number(adm.noofpeople) || 0) > 0)
        .map((adm: any) => ({ name: (adm.name ?? "").toString().trim(), people: Number(adm.noofpeople) || 0 }))
        .filter((adm: any) => adm.name != "");
    }

    if (typeof booking.admissions != "string" || booking.admissions.trim() == "") {
      return [];
    }

    return booking.admissions
      .split(",")
      .map((part: string) => part.trim())
      .filter((part: string) => part != "")
      .map((part: string) => {
        const split = part.match(/^(\d+)\s*x\s*(.+)$/i);

        //Anything that is not "N x Name" is counted as one of itself rather than thrown away.
        return split == null
          ? { name: part, people: 1 }
          : { name: split[2].trim(), people: Number(split[1]) || 0 };
      })
      .filter((adm: any) => adm.name != "" && adm.people > 0);
  }

  formatDateForExcel(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = ('0' + d.getUTCDate()).slice(-2);
    const month = ('0' + (d.getUTCMonth() + 1)).slice(-2);
    const year = d.getUTCFullYear();
    const hours = ('0' + d.getUTCHours()).slice(-2);
    const minutes = ('0' + d.getUTCMinutes()).slice(-2);
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  //The period cannot run backwards. Moving the From date past the To date takes the To date with
  //it, and the To picker will not open on anything earlier than the From date.
  fromDateChanged() {
    if (this.FromDate == null || this.ToDate == null) {
      return;
    }

    if (new Date(this.ToDate) < new Date(this.FromDate)) {
      this.ToDate = new Date(this.FromDate);
    }
  }
}
